import {
  defaultMapPresetPayload,
  mapPresetPayloadsEqual,
  normalizeMapPresetPayload,
} from "../../map/map-page-state.js";
import { parseCalculatorUserOverlayDocumentV4 } from "../calculator-user-layer-v4-contract.js";
import { presentCalculatorPresetChange } from "./calculator-preset-change-presentation.js";
import { presentFishydexPresetChange } from "./fishydex-preset-change-presentation.js";
import {
  decodeFishydexPresetPayload,
  defaultFishydexPresetPayload,
  migrateLegacyFishydexPresetPayload,
} from "./fishydex-payload.js";
import { assertMapPresetPayloadV1Bounds } from "./map-preset-payload-v1.js";

export {
  MAP_PRESET_GLOBAL_POINT_ICON_SCALE_RANGE_V1,
  MAP_PRESET_LAYER_POINT_ICON_SCALE_RANGE_V1,
  MAX_MAP_PRESET_ABSOLUTE_NUMBER_V1,
  MAX_MAP_PRESET_COLLECTION_ITEMS_V1,
  MAX_MAP_PRESET_IDENTIFIER_CHARS_V1,
  MAX_MAP_PRESET_PAYLOAD_BYTES_V1,
  MAX_MAP_PRESET_SEARCH_BYTES_V1,
  MAX_MAP_PRESET_SEARCH_DEPTH_V1,
  MAX_MAP_PRESET_SEARCH_LIST_ITEMS_V1,
  MAX_MAP_PRESET_SEARCH_NODES_V1,
  MAX_MAP_PRESET_SEARCH_STRING_CHARS_V1,
  MAX_MAP_PRESET_SEARCH_TOTAL_STRING_CHARS_V1,
} from "./map-preset-payload-v1.js";

export const PRESET_COLLECTION_KEYS = Object.freeze([
  "calculator-presets",
  "calculator-layouts",
  "calculator-overlays-v4",
  "map-presets",
  "fishydex-presets",
]);

export const CALCULATOR_PRESET_PAYLOAD_VERSION = 2;
export const CALCULATOR_LAYOUT_PAYLOAD_VERSION = 1;
export const MAX_CALCULATOR_PRESET_PAYLOAD_BYTES_V1 = 64 * 1024;
export const MAX_CALCULATOR_PRESET_STRING_CHARS_V1 = 512;
export const MAX_CALCULATOR_PRESET_LIST_ITEMS_V1 = 128;
export const MAX_CALCULATOR_PRESET_TOTAL_STRING_CHARS_V1 = 8 * 1024;

export const CALCULATOR_PRESET_SIGNAL_KEYS_V1 = Object.freeze([
  "active",
  "applyTradeModifiers",
  "backpack",
  "brand",
  "buff",
  "catchTimeActive",
  "catchTimeAfk",
  "chair",
  "discardGrade",
  "fishingMode",
  "harpoonZone",
  "hotspot",
  "float",
  "food",
  "level",
  "lifeskill_level",
  "lightstone_set",
  "mastery",
  "outfit",
  "pet1",
  "pet2",
  "pet3",
  "pet4",
  "pet5",
  "resources",
  "rod",
  "rodZone",
  "targetFish",
  "targetFishAmount",
  "targetFishPmfCount",
  "timespanAmount",
  "timespanUnit",
  "tradeDestinationNpc",
  "tradeDistanceBonus",
  "tradeOriginRegion",
  "tradePriceCurve",
  "trade_level",
]);

const CALCULATOR_PRESET_BOOLEAN_FIELDS = new Set([
  "active",
  "applyTradeModifiers",
  "brand",
]);
const CALCULATOR_PRESET_NUMBER_FIELDS = new Set([
  "catchTimeActive",
  "catchTimeAfk",
  "mastery",
  "resources",
  "targetFishAmount",
  "targetFishPmfCount",
  "timespanAmount",
  "tradeDistanceBonus",
  "tradePriceCurve",
]);
const CALCULATOR_PRESET_STRING_FIELDS = new Set([
  "backpack",
  "chair",
  "discardGrade",
  "fishingMode",
  "harpoonZone",
  "hotspot",
  "float",
  "lifeskill_level",
  "lightstone_set",
  "rod",
  "rodZone",
  "targetFish",
  "timespanUnit",
  "tradeDestinationNpc",
  "tradeOriginRegion",
  "trade_level",
]);
const CALCULATOR_PRESET_STRING_LIST_FIELDS = new Set(["buff", "food", "outfit"]);
const CALCULATOR_PRESET_PET_FIELDS = new Set(["pet1", "pet2", "pet3", "pet4", "pet5"]);
const CALCULATOR_PRESET_SIGNAL_FIELDS = new Set(CALCULATOR_PRESET_SIGNAL_KEYS_V1);
const CALCULATOR_PRESET_PET_STRING_FIELDS = new Set([
  "kind",
  "basedOn",
  "pet",
  "tier",
  "special",
  "talent",
]);
const CALCULATOR_PRESET_PET_ALLOWED_FIELDS = new Set([
  ...CALCULATOR_PRESET_PET_STRING_FIELDS,
  "packLeader",
  "displayLabel",
  "skills",
]);
const JSON_TEXT_ENCODER = new TextEncoder();

export const CALCULATOR_LAYOUT_SECTION_IDS_V1 = Object.freeze([
  "mode",
  "overview",
  "zone",
  "bite_time",
  "catch_time",
  "session",
  "distribution",
  "loot",
  "trade",
  "gear",
  "food",
  "buffs",
  "pets",
  "layers",
]);
const CALCULATOR_LAYOUT_SECTION_IDS = new Set(CALCULATOR_LAYOUT_SECTION_IDS_V1);

export const DEFAULT_CALCULATOR_LAYOUT_PAYLOAD = Object.freeze({
  version: CALCULATOR_LAYOUT_PAYLOAD_VERSION,
  customLayout: Object.freeze([
    Object.freeze([Object.freeze(["overview"])]),
    Object.freeze([Object.freeze(["zone"]), Object.freeze(["session"])]),
    Object.freeze([Object.freeze(["bite_time"]), Object.freeze(["loot"])]),
  ]),
});

const RUNTIME_DEFAULT_PRESET_DESCRIPTOR = Object.freeze([
  Object.freeze({ id: "default", name: "Default" }),
]);

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
}

function requireExactFields(value, fields, label) {
  if (!isPlainRecord(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const expected = [...fields].sort();
  if (
    actual.length !== expected.length
    || actual.some((field, index) => field !== expected[index])
  ) {
    throw new TypeError(`${label} has unsupported or missing fields`);
  }
  return value;
}

function requireAllowedFields(value, fields, label) {
  if (!isPlainRecord(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  for (const field of Object.keys(value)) {
    if (!fields.has(field)) {
      throw new TypeError(`${label} has unsupported field ${field}`);
    }
  }
  return value;
}

function canonicalJson(value, path = "$", seen = new WeakSet()) {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError(`${path} must contain only finite numbers`);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry, index) => canonicalJson(entry, `${path}[${index}]`, seen));
  }
  if (!isPlainRecord(value)) {
    throw new TypeError(`${path} must contain only JSON values`);
  }
  if (seen.has(value)) {
    throw new TypeError(`${path} must not contain a cycle`);
  }
  seen.add(value);
  const result = {};
  for (const key of Object.keys(value).sort()) {
    if (value[key] === undefined) {
      throw new TypeError(`${path}.${key} must not be undefined`);
    }
    result[key] = canonicalJson(value[key], `${path}.${key}`, seen);
  }
  seen.delete(value);
  return result;
}

function canonicalString(value) {
  return JSON.stringify(canonicalJson(value));
}

function requireCalculatorPresetString(value, label) {
  if (typeof value !== "string") {
    throw new TypeError(`${label} must be a string`);
  }
  let scalarCount = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xD800 && codeUnit <= 0xDBFF) {
      const nextCodeUnit = value.charCodeAt(index + 1);
      if (!(nextCodeUnit >= 0xDC00 && nextCodeUnit <= 0xDFFF)) {
        throw new TypeError(`${label} must contain valid Unicode scalar values`);
      }
      index += 1;
    } else if (codeUnit >= 0xDC00 && codeUnit <= 0xDFFF) {
      throw new TypeError(`${label} must contain valid Unicode scalar values`);
    }
    scalarCount += 1;
  }
  if (scalarCount > MAX_CALCULATOR_PRESET_STRING_CHARS_V1) {
    throw new TypeError(`${label} exceeds its character limit`);
  }
  return scalarCount;
}

function requireCalculatorPresetStringList(value, label) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${label} must be an array`);
  }
  if (value.length > MAX_CALCULATOR_PRESET_LIST_ITEMS_V1) {
    throw new TypeError(`${label} exceeds its item limit`);
  }
  return value.reduce(
    (total, entry, index) => total
      + requireCalculatorPresetString(entry, `${label}[${index}]`),
    0,
  );
}

function validateCalculatorPresetPet(value, label) {
  const pet = requireAllowedFields(value, CALCULATOR_PRESET_PET_ALLOWED_FIELDS, label);
  let stringChars = 0;
  if (pet.kind !== "catalog" && pet.kind !== "custom") {
    throw new TypeError(`${label}.kind must be catalog or custom`);
  }
  if (pet.displayLabel !== null) {
    stringChars += requireCalculatorPresetString(pet.displayLabel, `${label}.displayLabel`);
  }
  for (const field of Object.keys(pet)) {
    if (CALCULATOR_PRESET_PET_STRING_FIELDS.has(field)) {
      stringChars += requireCalculatorPresetString(pet[field], `${label}.${field}`);
    } else if (field === "packLeader" && typeof pet[field] !== "boolean") {
      throw new TypeError(`${label}.packLeader must be a boolean`);
    } else if (field === "skills") {
      stringChars += requireCalculatorPresetStringList(pet[field], `${label}.skills`);
    }
  }
  return stringChars;
}

function validateCalculatorPresetSignals(value) {
  const signals = requireAllowedFields(
    value,
    CALCULATOR_PRESET_SIGNAL_FIELDS,
    "Calculator preset signals",
  );
  let stringChars = 0;
  for (const field of Object.keys(signals)) {
    const fieldLabel = `Calculator preset signal ${field}`;
    if (CALCULATOR_PRESET_BOOLEAN_FIELDS.has(field)) {
      if (typeof signals[field] !== "boolean") {
        throw new TypeError(`${fieldLabel} must be a boolean`);
      }
    } else if (CALCULATOR_PRESET_NUMBER_FIELDS.has(field)) {
      if (typeof signals[field] !== "number" || !Number.isFinite(signals[field])) {
        throw new TypeError(`${fieldLabel} must be a finite number`);
      }
    } else if (CALCULATOR_PRESET_STRING_FIELDS.has(field)) {
      stringChars += requireCalculatorPresetString(signals[field], fieldLabel);
    } else if (CALCULATOR_PRESET_STRING_LIST_FIELDS.has(field)) {
      stringChars += requireCalculatorPresetStringList(signals[field], fieldLabel);
    } else if (CALCULATOR_PRESET_PET_FIELDS.has(field)) {
      stringChars += validateCalculatorPresetPet(signals[field], fieldLabel);
    } else if (
      field === "level"
      && (!Number.isInteger(signals[field])
        || signals[field] < -2_147_483_648
        || signals[field] > 2_147_483_647)
    ) {
      throw new TypeError(`${fieldLabel} must be a signed 32-bit integer`);
    }
  }
  if (stringChars > MAX_CALCULATOR_PRESET_TOTAL_STRING_CHARS_V1) {
    throw new TypeError("Calculator preset payload exceeds its total character limit");
  }
}

export function decodeCalculatorPresetPayload(value) {
  const payload = requireExactFields(
    value,
    ["version", "signals"],
    "Calculator preset payload",
  );
  if (payload.version !== 1 && payload.version !== CALCULATOR_PRESET_PAYLOAD_VERSION) {
    throw new TypeError(
      `Calculator preset payload version must be ${CALCULATOR_PRESET_PAYLOAD_VERSION}`,
    );
  }
  const migratedSignals = payload.version === 1
    ? migrateCalculatorSignalsV1ToV2(payload.signals)
    : payload.signals;
  validateCalculatorPresetSignals(migratedSignals);
  const signals = canonicalJson(migratedSignals, "$.signals");
  const decoded = { version: CALCULATOR_PRESET_PAYLOAD_VERSION, signals };
  const encodedLength = JSON_TEXT_ENCODER.encode(JSON.stringify(decoded)).byteLength;
  if (encodedLength > MAX_CALCULATOR_PRESET_PAYLOAD_BYTES_V1) {
    throw new TypeError("Calculator preset payload exceeds its byte limit");
  }
  return decoded;
}

export function createCalculatorPresetPayload(signals) {
  return decodeCalculatorPresetPayload({
    version: CALCULATOR_PRESET_PAYLOAD_VERSION,
    signals,
  });
}

function migrateLegacyCalculatorPresetPayload(value) {
  if (isPlainRecord(value) && (value.version === 1 || value.version === CALCULATOR_PRESET_PAYLOAD_VERSION)) {
    return decodeCalculatorPresetPayload(value);
  }
  return createCalculatorPresetPayload(migrateCalculatorSignalsV1ToV2(value));
}

function migrateCalculatorSignalsV1ToV2(value) {
  const signals = canonicalJson(value, "$.signals");
  for (const petKey of CALCULATOR_PRESET_PET_FIELDS) {
    const pet = signals[petKey];
    if (!isPlainRecord(pet)) continue;
    signals[petKey] = {
      kind: "catalog",
      displayLabel: null,
      basedOn: "",
      ...pet,
    };
  }
  return signals;
}

export function decodeCalculatorLayoutPresetPayload(value) {
  const payload = requireExactFields(
    value,
    ["version", "customLayout"],
    "Calculator layout preset payload",
  );
  if (payload.version !== CALCULATOR_LAYOUT_PAYLOAD_VERSION) {
    throw new TypeError(
      `Calculator layout preset version must be ${CALCULATOR_LAYOUT_PAYLOAD_VERSION}`,
    );
  }
  if (!Array.isArray(payload.customLayout)) {
    throw new TypeError("Calculator layout customLayout must be an array");
  }
  const seen = new Set();
  const customLayout = payload.customLayout.map((row, rowIndex) => {
    if (!Array.isArray(row) || row.length === 0) {
      throw new TypeError(`Calculator layout row ${rowIndex} must contain a column`);
    }
    return row.map((column, columnIndex) => {
      if (!Array.isArray(column) || column.length === 0) {
        throw new TypeError(
          `Calculator layout column ${rowIndex}.${columnIndex} must contain a section`,
        );
      }
      return column.map((sectionId) => {
        if (typeof sectionId !== "string" || !CALCULATOR_LAYOUT_SECTION_IDS.has(sectionId)) {
          throw new TypeError(`Calculator layout section ${String(sectionId)} is unsupported`);
        }
        if (seen.has(sectionId)) {
          throw new TypeError(`Calculator layout section ${sectionId} is duplicated`);
        }
        seen.add(sectionId);
        return sectionId;
      });
    });
  });
  return { version: CALCULATOR_LAYOUT_PAYLOAD_VERSION, customLayout };
}

function migrateLegacyCalculatorLayoutPayload(value) {
  if (isPlainRecord(value) && value.version === CALCULATOR_LAYOUT_PAYLOAD_VERSION) {
    return decodeCalculatorLayoutPresetPayload(value);
  }
  if (!isPlainRecord(value)) {
    throw new TypeError("Legacy calculator layout preset must be an object");
  }
  requireExactFields(value, ["custom_layout"], "Legacy calculator layout preset");
  return decodeCalculatorLayoutPresetPayload({
    version: CALCULATOR_LAYOUT_PAYLOAD_VERSION,
    customLayout: value.custom_layout,
  });
}

export function decodeMapPresetPayload(value) {
  assertMapPresetPayloadV1Bounds(value);
  const normalized = normalizeMapPresetPayload(value);
  if (canonicalString(value) !== canonicalString(normalized)) {
    throw new TypeError("Map preset payload must already match the canonical v1 shape");
  }
  return normalized;
}

function migrateLegacyMapPresetPayload(value) {
  return decodeMapPresetPayload(value);
}

function payloadFromRevision(value) {
  return isPlainRecord(value?.payload) ? value.payload : value;
}

function describeCalculator(value) {
  const payload = decodeCalculatorPresetPayload(payloadFromRevision(value));
  const mode = payload.signals.fishingMode === "harpoon"
    ? "Harpoon"
    : payload.signals.fishingMode === "hotspot"
      ? "Hotspot"
      : "Rod";
  return `${mode} · ${payload.signals.active === true ? "active" : "AFK"}`;
}

function describeLayout(value) {
  const payload = decodeCalculatorLayoutPresetPayload(payloadFromRevision(value));
  const count = payload.customLayout.flat(2).length;
  return `${count} section${count === 1 ? "" : "s"}`;
}

function describeOverlay(value) {
  const payload = parseCalculatorUserOverlayDocumentV4(payloadFromRevision(value));
  const count = payload.userLayer.operations.length;
  return `${payload.userLayer.name} · ${count} operation${count === 1 ? "" : "s"}`;
}

function describeMap(value) {
  const payload = decodeMapPresetPayload(payloadFromRevision(value));
  const count = payload.bridgedFilters.layerIdsVisible.length;
  return `${payload.bridgedUi.viewMode === "3d" ? "3D" : "2D"} · ${count} visible layer${count === 1 ? "" : "s"}`;
}

function describeFishydex(value) {
  const payload = decodeFishydexPresetPayload(payloadFromRevision(value));
  return `${payload.catchCounts.filter((entry) => entry.count > 0).length} caught · ${payload.favouriteIds.length} favourites`;
}

const contracts = new Map([
  ["calculator-presets", Object.freeze({
    key: "calculator-presets",
    info: Object.freeze({
      label: "Calculator presets",
      itemLabel: "Calculator preset",
      iconId: "fishy-settings-6-fill",
      fileBaseName: "fishystuff-calculator-presets",
    }),
    decodePayload: decodeCalculatorPresetPayload,
    migrateLegacyPayload: migrateLegacyCalculatorPresetPayload,
    payloadsEqual: (left, right) => canonicalString(decodeCalculatorPresetPayload(left))
      === canonicalString(decodeCalculatorPresetPayload(right)),
    fixedPresets: Object.freeze([]),
    runtimeFixedPresets: RUNTIME_DEFAULT_PRESET_DESCRIPTOR,
    describeRevision: describeCalculator,
    presentChange: presentCalculatorPresetChange,
  })],
  ["calculator-layouts", Object.freeze({
    key: "calculator-layouts",
    info: Object.freeze({
      label: "Workspace layouts",
      itemLabel: "Workspace layout",
      iconId: "fishy-layout-fill",
      fileBaseName: "fishystuff-calculator-layouts",
    }),
    decodePayload: decodeCalculatorLayoutPresetPayload,
    migrateLegacyPayload: migrateLegacyCalculatorLayoutPayload,
    payloadsEqual: (left, right) => canonicalString(decodeCalculatorLayoutPresetPayload(left))
      === canonicalString(decodeCalculatorLayoutPresetPayload(right)),
    fixedPresets: Object.freeze([
      Object.freeze({ id: "default", name: "Default", payload: DEFAULT_CALCULATOR_LAYOUT_PAYLOAD }),
    ]),
    describeRevision: describeLayout,
  })],
  ["calculator-overlays-v4", Object.freeze({
    key: "calculator-overlays-v4",
    info: Object.freeze({
      label: "Calculator overlays",
      itemLabel: "Calculator overlay",
      iconId: "fishy-edit-4-fill",
      fileBaseName: "fishystuff-calculator-overlay-v4-presets",
    }),
    decodePayload: parseCalculatorUserOverlayDocumentV4,
    migrateLegacyPayload: parseCalculatorUserOverlayDocumentV4,
    payloadsEqual: (left, right) => canonicalString(parseCalculatorUserOverlayDocumentV4(left))
      === canonicalString(parseCalculatorUserOverlayDocumentV4(right)),
    fixedPresets: Object.freeze([]),
    runtimeFixedPresets: RUNTIME_DEFAULT_PRESET_DESCRIPTOR,
    describeRevision: describeOverlay,
  })],
  ["map-presets", Object.freeze({
    key: "map-presets",
    info: Object.freeze({
      label: "Map presets",
      itemLabel: "Map preset",
      iconId: "fishy-map-view",
      fileBaseName: "fishystuff-map-presets",
    }),
    decodePayload: decodeMapPresetPayload,
    migrateLegacyPayload: migrateLegacyMapPresetPayload,
    payloadsEqual: mapPresetPayloadsEqual,
    promotionPayloadsEqual: (left, right) => canonicalString(decodeMapPresetPayload(left))
      === canonicalString(decodeMapPresetPayload(right)),
    fixedPresets: Object.freeze([
      Object.freeze({ id: "default", name: "Default", payload: defaultMapPresetPayload() }),
    ]),
    describeRevision: describeMap,
  })],
  ["fishydex-presets", Object.freeze({
    key: "fishydex-presets",
    info: Object.freeze({
      label: "Fishydex presets",
      itemLabel: "Fishydex preset",
      iconId: "fishy-nav-dex",
      fileBaseName: "fishystuff-fishydex-presets",
    }),
    decodePayload: decodeFishydexPresetPayload,
    migrateLegacyPayload: migrateLegacyFishydexPresetPayload,
    payloadsEqual: (left, right) => canonicalString(decodeFishydexPresetPayload(left))
      === canonicalString(decodeFishydexPresetPayload(right)),
    fixedPresets: Object.freeze([Object.freeze({
      id: "default",
      name: "Default",
      payload: Object.freeze({
        ...defaultFishydexPresetPayload(),
        favouriteIds: Object.freeze([]),
        catchCounts: Object.freeze([]),
        obtainedTitleIds: Object.freeze([]),
      }),
    })]),
    describeRevision: describeFishydex,
    presentChange: presentFishydexPresetChange,
  })],
]);

export function presetCollectionContracts() {
  return new Map(contracts);
}

export function presetCollectionContract(collectionKey) {
  const contract = contracts.get(String(collectionKey ?? ""));
  if (!contract) {
    throw new TypeError(`Unsupported preset collection ${String(collectionKey ?? "")}`);
  }
  return contract;
}
